import { useState } from 'react';
import swal from 'sweetalert';
import AccountingChangeRecordsTable from './AccountingChangeRecordsTable';
import AccountingPreviewTable from './AccountingPreviewTable';
import {
  applyPumpAccountingFix,
  friendlyFetchError,
  previewPumpAccountingFix,
} from './adminApi';
import { promptAccountingReason } from './accountingSwal';

function formatMoney(value) {
  if (value == null || Number.isNaN(Number(value))) return '—';
  return `₹${Number(value).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export default function GasCylinderOwnersFundTool({
  pumpId,
  fromDate,
  toDate,
  disabled = false,
  fixType,
  title,
  description,
  scanLabel,
  applyLabel,
  emptyMessage,
  findingType,
  risk = 'Low risk',
  removesRows = false,
  onPreviewStart,
  onPreviewEnd,
  onApplyStart,
  onApplyEnd,
  onCompleted,
}) {
  const [preview, setPreview] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [applying, setApplying] = useState(false);

  function validateDates() {
    if (!fromDate || !toDate) {
      swal('Dates required', 'Set From date and To date above before scanning.', 'warning');
      return false;
    }
    if (fromDate > toDate) {
      swal('Invalid dates', 'From date must be on or before To date.', 'warning');
      return false;
    }
    return true;
  }

  function findErrors() {
    if (!validateDates()) return;
    setScanning(true);
    setPreview(null);
    onPreviewStart?.();
    previewPumpAccountingFix(pumpId, fixType, {
      from_date: fromDate,
      to_date: toDate,
    })
      .then((res) => {
        setPreview(res);
        if (!res.status) {
          swal('Scan failed', res.message || 'Unable to scan', 'error');
          return;
        }
        const count = res.summary?.transactions ?? res.summary?.orphan_rows ?? 0;
        if (count === 0) {
          swal('No mismatches', res.message || emptyMessage, 'success');
        }
      })
      .catch((err) => swal('Scan failed', friendlyFetchError(err), 'error'))
      .finally(() => {
        setScanning(false);
        onPreviewEnd?.();
      });
  }

  async function applyFix() {
    if (!validateDates()) return;
    if (!preview?.status) {
      swal('Preview first', `Click "${scanLabel}" to preview changes before applying.`, 'warning');
      return;
    }
    if ((preview.rows_to_change ?? 0) === 0) {
      swal('Nothing to fix', preview.message || emptyMessage, 'info');
      return;
    }

    const reason = await promptAccountingReason(applyLabel, 'Explain why you are applying this fix (saved in Fix history).');
    if (!reason) return;

    setApplying(true);
    onApplyStart?.(applyLabel);
    try {
      const res = await applyPumpAccountingFix(
        pumpId,
        fixType,
        { from_date: fromDate, to_date: toDate },
        reason,
        findingType || fixType
      );
      onApplyEnd?.();

      if (res.status) {
        const caseId = res.case?.id;
        const doneMsg = removesRows
          ? 'Legacy or orphan rows were removed.'
          : 'Alignment rows were updated.';
        await swal(
          'Fix applied',
          `${res.message || doneMsg}${caseId ? `\n\nFix log #${caseId} — use Fix history to undo if needed.` : ''}`,
          'success'
        );
        setPreview(null);
        onCompleted?.(res);
      } else {
        await swal('Fix failed', res.message || 'Could not apply fix', 'error');
      }
    } catch (err) {
      onApplyEnd?.();
      await swal('Fix failed', friendlyFetchError(err), 'error');
    } finally {
      setApplying(false);
    }
  }

  const busy = scanning || applying || disabled;
  const hasIssues = (preview?.summary?.transactions ?? preview?.summary?.orphan_rows ?? 0) > 0
    || (preview?.rows_to_change ?? 0) > 0;

  return (
    <div className="admin-panel gas-cylinder-owners-fund-tool mb-3">
      <div className="d-flex flex-wrap justify-content-between align-items-start gap-2 mb-2">
        <div>
          <h6 className="mb-1">{title}</h6>
          <p className="small text-muted mb-0">{description}</p>
        </div>
        <span className="admin-badge audit-severity info">{risk}</span>
      </div>

      <div className="d-flex flex-wrap gap-2 mb-2">
        <button type="button" className="btn btn-outline-primary btn-sm" onClick={findErrors} disabled={busy}>
          {scanning ? 'Scanning…' : scanLabel}
        </button>
        <button type="button" className="btn btn-success btn-sm" onClick={applyFix} disabled={busy || !hasIssues}>
          {applying ? 'Applying…' : 'Apply fix'}
        </button>
      </div>

      {preview?.status && (
        <div className={`admin-alert-item mb-2 ${hasIssues ? 'warning' : 'info'}`}>
          <strong className="small">{preview.message}</strong>
          {preview.summary && (
            <ul className="small mb-0 mt-1 ps-3">
              <li>Bills / groups affected: {preview.summary.transactions ?? preview.summary.orphan_rows ?? 0}</li>
              <li>
                Ledger changes:{' '}
                {preview.summary.ledger_rows_to_remove != null
                  ? `${preview.summary.ledger_rows_to_remove} remove`
                  : preview.summary.ledger_rows_to_add != null
                    ? `${preview.summary.ledger_rows_to_add} add`
                    : (preview.rows_to_change ?? 0)}
              </li>
              {preview.summary.total_legacy_pl_debit != null && (
                <li>Legacy P&amp;L debits removed: ₹{Number(preview.summary.total_legacy_pl_debit).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</li>
              )}
              {preview.summary.total_legacy_pl_credit != null && (
                <li>Legacy P&amp;L credits removed: ₹{Number(preview.summary.total_legacy_pl_credit).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</li>
              )}
              {preview.summary.total_owners_fund_gap != null && (
                <li>Total gap cleared: {formatMoney(preview.summary.total_owners_fund_gap)}</li>
              )}
              {preview.summary.total_amount != null && (
                <li>Orphan row total: {formatMoney(preview.summary.total_amount)}</li>
              )}
            </ul>
          )}
        </div>
      )}

      {preview?.change_records?.length > 0 && (
        <div className="mb-2">
          <AccountingChangeRecordsTable
            records={preview.change_records}
            total={preview.change_records_total ?? preview.change_records.length}
            truncated={preview.change_records_truncated}
            source="preview"
          />
        </div>
      )}

      {preview?.sample_rows?.length > 0 && !preview?.change_records?.length && (
        <AccountingPreviewTable preview={preview} />
      )}
    </div>
  );
}
